import { useSelector } from 'react-redux';
import { useActualIncidentId } from './commonHooks';
import { selectUserGuid } from 'slices/commonSelectors';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import axios from 'axios';
import config from '../../../constants/apiConfig';
import { toast } from 'react-toastify';
import { toastConfig } from 'assets/data/config';
import { Status } from '../planningView/widgets/AssignmentList/AssignmentList';
export type PlannedResource = {
  id: string;
  leader: string;
  numberOfPersons: number;
  contactInfo: string;
  reportingInfo: string;
  has204a: boolean;
  /** Free-text Resource name when not linked to an incident resource. */
  documentationName?: string | null;
  isDocumentationEntry?: boolean;
  resourceBranch?: string;
  resourceDivision?: string;
  resourceGroup?: string;
  resourceStrikeTeam?: string;
  resourceTaskForce?: string;
  resourceAssignmentLocation?: string;
  resourceSpecialInstructions?: string;
  resourceSafetyPlanLocation?: string;
};

export type PlannedResourceRow = PlannedResource & {
  name: string;
  type?: string;
  walaId: string | null;
};

export type WalaLog = {
  id: string;
  status: Status;
  proposedStatus: Status;
  updatedAt: string;
  updatedByUserName: string;
  updatedByEmail: string;
}[];

type CommonWorkAssignmentListAttachmentBody = {
  proposedStatus?: Status;
  sectionChief?: string;
  sectionChiefContact?: string;
  branchDirector?: string;
  branchDirectorContact?: string;
  divisionGroupSupervisor?: string;
  divisionGroupSupervisorContact?: string;
  workGroupId?: string;
  branch?: string;
  division?: string;
  walaGroup?: string;
  stagingArea?: string;
  resources?: string[]; // intermediary step: I want to merge resources and plannedResources
  plannedResources?: PlannedResource[]; // intermediary step: I want to merge resources and plannedResources
  medicalCommunication?: string;
  evacuationCommunication?: string;
  otherCommunication?: string;
  specialInstructions?: string;
};

export type WorkAssignmentListAttachmentCreateBody =
  CommonWorkAssignmentListAttachmentBody & {
    workGroupId: string; // required for creation
    periodNumber: number; // required for creation
  };

export type WorkAssignmentListAttachmentUpdateBody =
  CommonWorkAssignmentListAttachmentBody & {
    walaId: string; // required for update
    /** Bump tip updatedAt/updatedBy without content field deltas (e.g. Box 6). */
    touch?: boolean;
  };

export type WalaSignature = {
  name: string;
  role: string;
  signedAt: number;
};

export type WorkAssignmentListAttachmentData = {
  id: string;
  walaId: string;
  incidentId: string;
  userGuid: string;
  workGroupId: string;
  branch: string;
  division: string;
  walaGroup: string;
  stagingArea: string;
  status: Status;
  proposedStatus: Status;
  archived?: boolean;
  sectionChief?: string;
  sectionChiefContact?: string;
  branchDirector?: string;
  branchDirectorContact?: string;
  divisionGroupSupervisor?: string;
  divisionGroupSupervisorContact?: string;
  workGroupName: string;
  resources: string[];
  plannedResources: PlannedResource[];
  medicalCommunication?: string;
  evacuationCommunication?: string;
  otherCommunication?: string;
  operationalPeriodNumber: number;
  specialInstructions?: string;
  signatures?: WalaSignature[];
  resourceSnapshot?: PlannedResource[] | null;
  updatedAt?: string;
  updatedByUserName?: string | null;
};

export type WalaVersion = {
  id: string;
  walaId: string;
  itemId: string;
  branch?: string | null;
  division?: string | null;
  stagingArea?: string | null;
  walaGroup?: string | null;
  sectionChief?: string | null;
  sectionChiefContact?: string | null;
  branchDirector?: string | null;
  branchDirectorContact?: string | null;
  divisionGroupSupervisor?: string | null;
  divisionGroupSupervisorContact?: string | null;
  proposedStatus?: Status | null;
  status?: Status | null;
  workGroupId?: string | null;
  medicalCommunication?: string | null;
  evacuationCommunication?: string | null;
  otherCommunication?: string | null;
  specialInstructions?: string | null;
  operationalPeriodNumber?: number | null;
  signatures?: WalaSignature[];
  resourceSnapshot?: PlannedResource[] | null;
  createdAt: string;
  updatedAt: string;
  updatedByUserName?: string | null;
  updatedByUserEmail?: string | null;
};

export const keys = {
  base: 'incident_workAssignmentListAttachment',
  get all() {
    return [keys.base];
  },
  list: (incidentId: string, userId: string) => [keys.base, incidentId, userId],
} as const;

export const useWalaLog = (walaId: string) => {
  const incidentId = useActualIncidentId();
  return useQuery<WalaLog>({
    queryKey: ['walaLog', walaId],
    queryFn: async () => {
      if (!incidentId) return [];
      const { data } = await axios.get(
        `${config.apiGateway.ics_work_assignment_list_attachment}/${incidentId}/${walaId}/log`
      );
      return data;
    },
    enabled: !!walaId && !!incidentId,
  });
};

export const useWalaVersions = (walaId: string) => {
  const incidentId = useActualIncidentId();
  return useQuery<WalaVersion[]>({
    queryKey: ['walaVersions', walaId],
    queryFn: async () => {
      if (!incidentId) return [];
      const { data } = await axios.get(
        `${config.apiGateway.ics_work_assignment_list_attachment}/${incidentId}/${walaId}/versions`
      );
      return data;
    },
    enabled: !!walaId && !!incidentId,
  });
};

const invalidateWalaQueries = (
  queryClient: ReturnType<typeof useQueryClient>,
  incidentId: string,
  userGuid: string,
  walaId?: string
) => {
  queryClient.invalidateQueries({
    queryKey: keys.list(incidentId, userGuid),
  });
  if (walaId) {
    queryClient.invalidateQueries({ queryKey: ['walaVersions', walaId] });
    queryClient.invalidateQueries({ queryKey: ['walaLog', walaId] });
  }
};

export type CreateSignedWalaVersionBody =
  WorkAssignmentListAttachmentUpdateBody & {
    preparedBy: WalaSignature & { role: 'Prepared By' };
  };

export const useCreateSignedWalaVersion = () => {
  const incidentId = useActualIncidentId();
  const user_guid = useSelector(selectUserGuid);
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (data: CreateSignedWalaVersionBody) => {
      const { walaId, ...body } = data;
      return await axios.post(
        `${config.apiGateway.ics_work_assignment_list_attachment}/${incidentId}/${walaId}/versions/signed`,
        body
      );
    },
    onSuccess: (_data, variables) => {
      invalidateWalaQueries(
        queryClient,
        incidentId,
        user_guid,
        variables.walaId
      );
      toast.success('Signed version created.', toastConfig);
    },
    onError: (error: any) => {
      toast.error(
        error?.response?.data?.error?.message || error.message,
        toastConfig
      );
    },
  });
};

export const useCreateUnsignedWalaVersion = () => {
  const incidentId = useActualIncidentId();
  const user_guid = useSelector(selectUserGuid);
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (walaId: string) => {
      return await axios.post(
        `${config.apiGateway.ics_work_assignment_list_attachment}/${incidentId}/${walaId}/versions/unsigned`
      );
    },
    onSuccess: (_data, walaId) => {
      invalidateWalaQueries(queryClient, incidentId, user_guid, walaId);
    },
    onError: (error: any) => {
      toast.error(
        error?.response?.data?.error?.message || error.message,
        toastConfig
      );
    },
  });
};

export const useAppendWalaSignature = () => {
  const incidentId = useActualIncidentId();
  const user_guid = useSelector(selectUserGuid);
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (data: {
      walaId: string;
      signature: WalaSignature & {
        role: 'Planning Section Chief' | 'Operations Section Chief';
      };
    }) => {
      return await axios.patch(
        `${config.apiGateway.ics_work_assignment_list_attachment}/${incidentId}/${data.walaId}/signatures`,
        { signature: data.signature }
      );
    },
    onSuccess: (_data, variables) => {
      invalidateWalaQueries(
        queryClient,
        incidentId,
        user_guid,
        variables.walaId
      );
      toast.success('Signature added.', toastConfig);
    },
    onError: (error: any) => {
      toast.error(
        error?.response?.data?.error?.message || error.message,
        toastConfig
      );
    },
  });
};

export type Ics204aVersionPayload = {
  leader?: string | null;
  numberOfPersons?: number | null;
  contactInfo?: string | null;
  reportingInfo?: string | null;
  has204a?: boolean | null;
  documentationName?: string | null;
  resourceBranch?: string | null;
  resourceDivision?: string | null;
  resourceGroup?: string | null;
  resourceStrikeTeam?: string | null;
  resourceTaskForce?: string | null;
  resourceAssignmentLocation?: string | null;
  resourceSpecialInstructions?: string | null;
  resourceSafetyPlanLocation?: string | null;
};

export type Ics204aVersion = {
  id: string;
  walaId: string;
  plannedResourceKey: string;
  payload?: Ics204aVersionPayload | null;
  signatures?: WalaSignature[];
  createdAt: string;
  updatedAt: string;
  updatedByUserName?: string | null;
  updatedByUserEmail?: string | null;
};

const ics204aVersionsKey = (walaId: string, plannedResourceKey: string) => [
  'ics204aVersions',
  walaId,
  plannedResourceKey,
];

const invalidateIcs204aQueries = (
  queryClient: ReturnType<typeof useQueryClient>,
  incidentId: string,
  userGuid: string,
  walaId: string,
  plannedResourceKey: string
) => {
  invalidateWalaQueries(queryClient, incidentId, userGuid, walaId);
  queryClient.invalidateQueries({
    queryKey: ics204aVersionsKey(walaId, plannedResourceKey),
  });
};

export const useIcs204aVersions = (
  walaId: string,
  plannedResourceKey: string
) => {
  const incidentId = useActualIncidentId();
  return useQuery<Ics204aVersion[]>({
    queryKey: ics204aVersionsKey(walaId, plannedResourceKey),
    queryFn: async () => {
      if (!incidentId || !walaId || !plannedResourceKey) return [];
      const { data } = await axios.get(
        `${config.apiGateway.ics_work_assignment_list_attachment}/${incidentId}/${walaId}/204a/${plannedResourceKey}/versions`
      );
      return data;
    },
    enabled: !!walaId && !!plannedResourceKey && !!incidentId,
  });
};

export type CreateSignedIcs204aVersionBody = Ics204aVersionPayload & {
  walaId: string;
  plannedResourceKey: string;
  preparedBy: WalaSignature & { role: 'Prepared By' };
};

export const useCreateSignedIcs204aVersion = () => {
  const incidentId = useActualIncidentId();
  const user_guid = useSelector(selectUserGuid);
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (data: CreateSignedIcs204aVersionBody) => {
      const { walaId, plannedResourceKey, ...body } = data;
      return await axios.post(
        `${config.apiGateway.ics_work_assignment_list_attachment}/${incidentId}/${walaId}/204a/${plannedResourceKey}/versions/signed`,
        body
      );
    },
    onSuccess: (_data, variables) => {
      invalidateIcs204aQueries(
        queryClient,
        incidentId,
        user_guid,
        variables.walaId,
        variables.plannedResourceKey
      );
      toast.success('Signed ICS-204A version created.', toastConfig);
    },
    onError: (error: any) => {
      toast.error(
        error?.response?.data?.error?.message || error.message,
        toastConfig
      );
    },
  });
};

export const useCreateUnsignedIcs204aVersion = () => {
  const incidentId = useActualIncidentId();
  const user_guid = useSelector(selectUserGuid);
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (data: {
      walaId: string;
      plannedResourceKey: string;
    }) => {
      return await axios.post(
        `${config.apiGateway.ics_work_assignment_list_attachment}/${incidentId}/${data.walaId}/204a/${data.plannedResourceKey}/versions/unsigned`
      );
    },
    onSuccess: (_data, variables) => {
      invalidateIcs204aQueries(
        queryClient,
        incidentId,
        user_guid,
        variables.walaId,
        variables.plannedResourceKey
      );
    },
    onError: (error: any) => {
      toast.error(
        error?.response?.data?.error?.message || error.message,
        toastConfig
      );
    },
  });
};

export const useAppendIcs204aSignature = () => {
  const incidentId = useActualIncidentId();
  const user_guid = useSelector(selectUserGuid);
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (data: {
      walaId: string;
      plannedResourceKey: string;
      signature: WalaSignature & {
        role: 'Planning Section Chief' | 'Operations Section Chief';
      };
    }) => {
      return await axios.patch(
        `${config.apiGateway.ics_work_assignment_list_attachment}/${incidentId}/${data.walaId}/204a/${data.plannedResourceKey}/signatures`,
        { signature: data.signature }
      );
    },
    onSuccess: (_data, variables) => {
      invalidateIcs204aQueries(
        queryClient,
        incidentId,
        user_guid,
        variables.walaId,
        variables.plannedResourceKey
      );
      toast.success('Signature added.', toastConfig);
    },
    onError: (error: any) => {
      toast.error(
        error?.response?.data?.error?.message || error.message,
        toastConfig
      );
    },
  });
};

export const useWorkAssignmentListAttachments = () => {
  const incidentId = useActualIncidentId();
  const user_guid = useSelector(selectUserGuid);
  return useQuery<WorkAssignmentListAttachmentData[]>({
    queryKey: keys.list(incidentId, user_guid),
    queryFn: async () => {
      if (!incidentId) return [];
      const { data } = await axios.get(
        `${config.apiGateway.ics_work_assignment_list_attachment}/${incidentId}`
      );
      return data;
    },
    enabled: !!incidentId,
  });
};

export const useCreateWorkAssignmentListAttachment = () => {
  const incidentId = useActualIncidentId();
  const user_guid = useSelector(selectUserGuid);
  const queryClient = useQueryClient();
  const mutation = useMutation({
    mutationFn: async (data: WorkAssignmentListAttachmentCreateBody) => {
      return await axios.post(
        `${config.apiGateway.ics_work_assignment_list_attachment}/${incidentId}`,
        data
      );
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: keys.list(incidentId, user_guid),
      });
    },
    onError: (error: any) => {
      if (!incidentId) {
        toast.error(
          "You don't have any incidents in your organization. Please create one to proceed.",
          toastConfig
        );
      } else {
        toast.error(error.message, toastConfig);
      }
    },
  });
  return mutation;
};

export const useUpdateWorkAssignmentListAttachment = () => {
  const incidentId = useActualIncidentId();
  const user_guid = useSelector(selectUserGuid);
  const queryClient = useQueryClient();
  const mutation = useMutation({
    mutationFn: async (data: WorkAssignmentListAttachmentUpdateBody) => {
      return await axios.put(
        `${config.apiGateway.ics_work_assignment_list_attachment}/${incidentId}/${data.walaId}`,
        data
      );
    },
    onSuccess: (_data, variables) => {
      invalidateWalaQueries(
        queryClient,
        incidentId,
        user_guid,
        variables.walaId
      );
    },
    onError: (error: any) => {
      if (!incidentId) {
        toast.error(
          "You don't have any incidents in your organization. Please create one to proceed.",
          toastConfig
        );
      } else {
        toast.error(
          error?.response?.data?.error?.message || error.message,
          toastConfig
        );
      }
    },
  });
  return mutation;
};

/** Soft-deletes an ICS-204 card so it leaves the Assignment List. */
export const useArchiveWorkAssignmentListAttachment = () => {
  const incidentId = useActualIncidentId();
  const user_guid = useSelector(selectUserGuid);
  const queryClient = useQueryClient();
  const mutation = useMutation({
    mutationFn: async (walaId: string) => {
      return await axios.post(
        `${config.apiGateway.ics_work_assignment_list_attachment}/${incidentId}/${walaId}/archive`
      );
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: keys.list(incidentId, user_guid),
      });
    },
    onError: (error: any) => {
      if (!incidentId) {
        toast.error(
          "You don't have any incidents in your organization. Please create one to proceed.",
          toastConfig
        );
      } else {
        toast.error(error.message, toastConfig);
      }
    },
  });
  return mutation;
};

export const useApproveWorkAssignmentListAttachment = () => {
  const incidentId = useActualIncidentId();
  const user_guid = useSelector(selectUserGuid);
  const queryClient = useQueryClient();
  const mutation = useMutation({
    mutationFn: async (attachmentId: string) => {
      return await axios.post(
        `${config.apiGateway.ics_work_assignment_list_attachment}/${incidentId}/${attachmentId}/approve`
      );
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: keys.list(incidentId, user_guid),
      });
    },
    onError: (error: any) => {
      if (!incidentId) {
        toast.error(
          "You don't have any incidents in your organization. Please create one to proceed.",
          toastConfig
        );
      } else {
        toast.error(error.message, toastConfig);
      }
    },
  });
  return mutation;
};

export const useDenyWorkAssignmentListAttachment = () => {
  const incidentId = useActualIncidentId();
  const user_guid = useSelector(selectUserGuid);
  const queryClient = useQueryClient();
  const mutation = useMutation({
    mutationFn: async (attachmentId: string) => {
      return await axios.post(
        `${config.apiGateway.ics_work_assignment_list_attachment}/${incidentId}/${attachmentId}/deny`
      );
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: keys.list(incidentId, user_guid),
      });
    },
    onError: (error: any) => {
      if (!incidentId) {
        toast.error(
          "You don't have any incidents in your organization. Please create one to proceed.",
          toastConfig
        );
      } else {
        toast.error(error.message, toastConfig);
      }
    },
  });
  return mutation;
};

export const useUpdatePlannedResource = (plannedResourceId: string) => {
  const incidentId = useActualIncidentId();
  const user_guid = useSelector(selectUserGuid);
  const queryClient = useQueryClient();
  const mutation = useMutation({
    mutationFn: async (data: PlannedResource) => {
      return await axios.put(
        `${config.apiGateway.ics_planned_resource}/${plannedResourceId}`,
        data
      );
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: keys.list(incidentId, user_guid),
      });
    },
    onError: (error: any) => {
      toast.error(error.message, toastConfig);
    },
  });
  return mutation;
};

export const ICS_204_CG_FORM_NAME = 'ICS_204_CG';

type Ics204PreviewResource = {
  name: string;
  leader: string;
  quantity: number;
  contactInfo: string;
  reportingInfo: string;
  has204a: boolean;
};

type Ics204PreviewCommunication = {
  commName: string;
  contactInfo: string;
};

/**
 * One page of the 204 as the server paginated it. The PDF is what gets shown, so this is the structured record of the
 * same content, used by tests to assert where page breaks land without parsing a document.
 */
export type Ics204PreviewPage = {
  pageNumber: number;
  totalPages: number;
  footerLeft: string;
  incidentName: string;
  opFrom: string;
  opTo: string;
  sectionChiefName: string;
  sectionChiefContact: string;
  branchDirectorName: string;
  branchDirectorContact: string;
  divisionGroupSupervisorName: string;
  divisionGroupSupervisorContact: string;
  branch: string;
  division: string;
  group: string;
  stagingArea: string;
  workAssignments: string;
  specialInstructions: string;
  emergencyMedical: string;
  emergencyEvacuation: string;
  emergencyOther: string;
  resources: Ics204PreviewResource[];
  communications: Ics204PreviewCommunication[];
  preparedBy: string;
  reviewedByPSC: string;
  reviewedByOSCISC: string;
};

export type Ics204PdfExportWithPreview = {
  pdfBase64: string;
  pages: Ics204PreviewPage[];
};

export type Ics204DocxExportResult = {
  docxBase64: string;
  pages?: Ics204PreviewPage[];
};

export async function postExportWorkAssignmentListAttachment(
  incidentId: string,
  periodNumber: number,
  walaId: string,
  options?: { includePreviewPages?: boolean; format?: 'pdf' | 'docx' }
): Promise<string | Ics204PdfExportWithPreview | Ics204DocxExportResult> {
  const response = await axios.post(
    `${config.apiGateway.ics_work_assignment_list_attachment}/${incidentId}/${walaId}/export`,
    {
      periodNumber,
      skipWorkspaceUpload: true,
      includePreviewPages: options?.includePreviewPages === true,
      format: options?.format ?? 'pdf',
    }
  );
  return response.data;
}
